import React, { useContext, useState, useEffect } from "react"

import Paper from "@material-ui/core/Paper"
import Container from "@material-ui/core/Container"
import Grid from "@material-ui/core/Grid"
import Tabs from "@material-ui/core/Tabs"
import Tab from "@material-ui/core/Tab"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography";

import useTickets from "../../hooks/useTickets"

import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import Chart from "./Chart"

const formatDuration = totalSeconds => {
	const seconds = Math.round(totalSeconds || 0);
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}min`;
	const hours = Math.floor(minutes / 60);
	return `${hours}h ${minutes % 60}min`;
};

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	fixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 240,
	},
	customFixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 120,
	},
	customFixedHeightPaperLg: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: "100%",
	},
}))

const Dashboard = () => {
	const classes = useStyles()

	const { user } = useContext(AuthContext);
	var userQueueIds = [];

	if (user.queues && user.queues.length > 0) {
		userQueueIds = user.queues.map(q => q.id);
	}

	const GetTickets = (status, showAll, withUnreadMessages) => {

		const { count } = useTickets({
			status: status,
			showAll: showAll,
			withUnreadMessages: withUnreadMessages,
			queueIds: JSON.stringify(userQueueIds)
		});
		return count;
	}

	const openCount = GetTickets("open", "true", "false");
	const pendingCount = GetTickets("pending", "true", "false");
	const closedCount = GetTickets("closed", "true", "false");

	const [tab, setTab] = useState("attendance");
	const [metrics, setMetrics] = useState(null);

	useEffect(() => {
		const fetchMetrics = async () => {
			try {
				const { data } = await api.get("/dashboard/metrics");
				setMetrics(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchMetrics();
	}, []);

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				<Tabs
					value={tab}
					onChange={(e, value) => setTab(value)}
					indicatorColor="primary"
					textColor="primary"
					style={{ marginBottom: 16 }}
				>
					<Tab value="attendance" label={i18n.t("dashboard.tabs.attendance")} />
					<Tab value="metrics" label={i18n.t("dashboard.tabs.metrics")} />
				</Tabs>
				{tab === "attendance" && (
				<Grid container spacing={3}>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.inAttendance.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{openCount}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.waiting.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{pendingCount}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.closed.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{closedCount}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12}>
						<Paper className={classes.fixedHeightPaper}>
							<Chart />
						</Paper>
					</Grid>
				</Grid>
				)}
				{tab === "metrics" && (
				<Grid container spacing={3}>
					<Grid item xs={6}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.metrics.avgHandlingTime")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{metrics ? formatDuration(metrics.avgHandlingTimeSeconds) : "..."}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={6}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.metrics.closedTickets")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{metrics ? metrics.closedTicketsCount : "..."}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={6}>
						<Paper className={classes.fixedHeightPaper}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.metrics.byUser")}
							</Typography>
							{metrics?.byUser?.length > 0 ? (
								<List dense>
									{metrics.byUser.map(entry => (
										<ListItem key={entry.id} divider>
											<ListItemText primary={entry.name} />
											<Typography variant="h6">{entry.count}</Typography>
										</ListItem>
									))}
								</List>
							) : (
								<Typography variant="body2" color="textSecondary">
									{i18n.t("dashboard.metrics.empty")}
								</Typography>
							)}
						</Paper>
					</Grid>
					<Grid item xs={6}>
						<Paper className={classes.fixedHeightPaper}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.metrics.byQueue")}
							</Typography>
							{metrics?.byQueue?.length > 0 ? (
								<List dense>
									{metrics.byQueue.map(entry => (
										<ListItem key={entry.id} divider>
											<ListItemText primary={entry.name} />
											<Typography variant="h6">{entry.count}</Typography>
										</ListItem>
									))}
								</List>
							) : (
								<Typography variant="body2" color="textSecondary">
									{i18n.t("dashboard.metrics.empty")}
								</Typography>
							)}
						</Paper>
					</Grid>
				</Grid>
				)}
			</Container>
		</div>
	)
}

export default Dashboard